import React, { useMemo } from "react";

import {
  faWrench,
  faPaperPlane as lightFaPaperPlane,
} from "@fortawesome/pro-light-svg-icons";
import {
  faCircle,
  faCommentsDollar,
  faDollar,
  faExclamationCircle,
  faEye,
  faMoneyBills,
  faPaperPlane,
  faPencil,
  faRefresh,
  faUpDown,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ICellRendererParams } from "ag-grid-community";
import Big from "big.js";
import { get } from "lodash";
import maxBy from "lodash/maxBy";
import sumBy from "lodash/sumBy";
import { useDispatch, useSelector } from "react-redux";

import { openDispatchingModal } from "actions";

import { BillingDocumentAction } from "actions/billingDocuments";

import {
  ActionButton,
  ActionButtonContainer,
} from "components/AgGrid/ActionButton";
import { ColumnType } from "components/AgGrid/constants";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { MultiButton, SlimButton } from "components/Button";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";

import { ACTION_COLUMN_NAME } from "constants/aggrid";
import { BillingScreens, GstMethod } from "constants/billing";
import {
  BillingDocumentIntegrationStatus,
  BillingRole,
  BillingDocumentIntegrationStatusColorMap,
  BillingDocumentReportJobStatus,
  BillingDocumentStatusColorMap,
  BillingDocumentType,
  BillingDocumentTypeColorMap,
  InactiveIntegrationStatuses,
} from "constants/billingDocuments";
import { BillingRunStatus } from "constants/billingRuns";
import { IssuerColId, RecipientColId } from "constants/columns";
import { SystemLabelName } from "constants/deploymentLabels";
import { BusinessModalSection, ModalTypes } from "constants/navigation";
import { SaleType } from "constants/sale";
import { MANUAL_TEMPLATE_NAME } from "constants/sundryTemplates";
import { colors, space } from "constants/theme";

import formatters, { floatFormatter } from "lib/agGrid/formatters";
import { getters } from "lib/agGrid";
import { emailCountRenderer } from "lib/agGrid/renderers";
import { hasChangesRenderer } from "lib/agGrid/renderers/changesRenderer";
import { StatusRenderer } from "lib/agGrid/renderers/statusRenderer";
import { AggridColDefContext } from "lib/agGrid/types";
import { caseInsensitiveCompare } from "lib/compare";
import {
  buildPaymentAdviceButtons,
  getPaymentAdviceDirection,
  groupPayments,
} from "lib/billingDocumentPaymentAdvice";
import {
  getBillingDocumentReportJobUrl,
  getPaymentReferenceLabel,
  goToBillingStep,
  openEditBusinessModal,
  openModalLink,
} from "lib/navigation";
import { buildSundryTemplateOptions } from "lib/sundries";
import { dateTimeStringToDateTime, getDaysDifferent } from "lib/timeFormats";
import toast from "lib/toast";

import {
  getDefaultBillingSettings,
  getCurrentSale,
  getCurrentSpeciesId,
  getLabels,
  getRoundingAdjustmentManualChargeIdsByBillingDocumentId,
  getRelevantSundryTemplatesByBillingDocumentId,
} from "selectors";

import { getBillingRunById } from "selectors/billingRuns";

import {
  useHasManualChargesPermission,
  useHasRetainedProceedsPermission,
} from "hooks";

/**
 * Returns the difference between the integration amount (Xero, etc.) and the AgriNous amount
 * (integration minus AgriNous), rounded to the cent.
 *
 * Computes with Big.js to avoid IEEE 754 precision errors. 
 */
export function centsDifferenceToDollars(
  integrationCents: Big,
  agrinousCents: Big,
): number {
  // `|| 0` turns the -0 Big produces when rounding a tiny negative into 0.
  return (
    integrationCents.minus(agrinousCents).div(100).round(2).toNumber() || 0
  );
}

export const RetainedProceedsColId = {
  GL_CODE: "51ef761a-dc91-49fe-ac0d-64ba7a9d6ca4",
  INTEGRATION_STATUS: "f5380ebf-34c0-48e8-8f3a-ce3e8f347f97",
  NUMBER: "b8e8505c-8386-495e-abfd-30de5676202a",
  TOTAL_AMOUNT_CENTS: "c268b47f-4973-4232-87f3-dfa0da8cd328",
};

function RetainedProceedsDocumentNumberRenderer(props: ICellRendererParams) {
  const { node, value } = props;
  const billingSettings = useSelector(getDefaultBillingSettings);
  if (!value || node.group) {
    return null;
  }

  return `${
    (billingSettings && billingSettings.retainedProceedsPrefix) || "RP"
  }${value}`;
}

export const RetainedProceedsColumnDef = {
  [RetainedProceedsColId.GL_CODE]: {
    colId: RetainedProceedsColId.GL_CODE,
    field: "billingDocument.proceedsRetainedByBillingDocument.glCode",
    headerName: "GL Code",
  },
  [RetainedProceedsColId.INTEGRATION_STATUS]: {
    colId: RetainedProceedsColId.INTEGRATION_STATUS,
    field:
      "billingDocument.proceedsRetainedByBillingDocument.integrationStatus",
    headerName: "Integration Status",
    cellRenderer: StatusRenderer,
    context: {
      statusRendererProps: {
        colorMap: BillingDocumentIntegrationStatusColorMap,
      },
    } satisfies AggridColDefContext,
  },
  [RetainedProceedsColId.NUMBER]: {
    colId: RetainedProceedsColId.NUMBER,
    field: "billingDocument.proceedsRetainedByBillingDocument.number",
    headerName: "Document Number",
    cellRenderer: RetainedProceedsDocumentNumberRenderer,
    comparator: caseInsensitiveCompare,
  },
  [RetainedProceedsColId.TOTAL_AMOUNT_CENTS]: {
    colId: RetainedProceedsColId.TOTAL_AMOUNT_CENTS,
    field: "billingDocument.proceedsRetainedByBillingDocument.totalAmountCents",
    headerName: "Total Amount",
    type: ColumnType.CURRENCY_FROM_CENTS,
  },
};

export const BillingDocumentColId = {
  ACTIONS: "f48332b5-0c17-4b02-8cdb-cdaebda4de2f",
  ACTION_FIX: "95c5f3ef-622a-4a32-8917-8c2bec82c2d5",
  ACTION_FIX_BILLING_DOCUMENT: "bb0dfb32-9480-4794-a1cb-31b7707233a4",
  ACTION_TAKEN: "b31e8ab4-68bc-4557-a7f4-460c2783de94",
  ACTIVITY: "63b37729-0813-467e-977c-0d4222fe26be",
  BILLING_DOCUMENT: "d5d817c5-64aa-4ff9-9510-2554faa91625",
  BILLING_REFERENCE: "33607cf4-ef5e-4b34-876e-84ac45f14096",
  BILLING_ROLE: "d0e6af95-729c-4a1c-a550-74f239c29fcc",
  CALCULATED_BILLING_REFERENCE: "684ea629-0638-4e02-b43b-326299ec2673",
  CREATED: "549ac4c7-dc1c-47dd-8f3d-a0172ce374f7",
  DAYS_LATE: "c8a3bd08-596c-4c37-80b4-149306590e1e",
  DAYS_SINCE_DUE: "27dc73e3-91ac-4ea3-9536-790386578caf",
  DAYS_SINCE_PAYMENT: "d409b727-beb7-4594-b426-b583d7ae84c6",
  DOCUMENT_DOWNLOAD: "efc0b4e6-23b1-43b3-a69d-cd4481784da6",
  DOCUMENT_NUMBER: "02379ad0-3ee4-403e-b2bd-728373915909",
  DOCUMENT_TYPE: "22fe2f48-9804-42ea-815b-44489ffb2754",
  DUE_DATE: "b96f531a-7149-47d7-b412-29908d19adf3",
  EMAIL_COUNT: "4d12ed46-ab95-42f3-a7bb-0ef5e352f5d4",
  HAS_CHANGES: "f7968824-9dd8-4a83-a2c2-f570f1f8ae1f",
  INTEGRATION_AMOUNT_DIFFERENCE: "44d849af-f12e-4885-9a0a-e85e1483f836",
  INTEGRATION_DUE_DIFFERENCE: "fefa419f-042a-4e53-98b9-9b81510e2ced",
  INTEGRATION_STATUS: "f435180e-250f-4a9e-a5f8-138f6ebe87dd",
  ISSUED: "a61e7e5a-6493-4ce0-93a4-7be9aaacea81",
  LAST_MODIFIED: "b4274b19-7765-4340-8ba0-2618bc0453f6",
  LAST_PAID_INTEREST_DATE: "68a566cc-fe8d-4ce5-b522-5c8d7e81b500",
  LIVESTOCK_SALES: "1bf5de22-9df4-42c7-a8e7-efacae66d39f",
  OPEN_IN_NEW_WINDOW: "3d584e61-ce27-4b4e-b6ad-c4fc69562058",
  PAID: "60f5f9fc-10d4-4846-9b84-f464d1f576ed",
  PAID_DATE: "4f53300d-bbf6-4846-9138-10f1f43595b2",
  PAYMENT_REFS: "c3e8a1f2-7d4b-4e9f-a5b6-8c9d0e1f2a3b",
  PLANNED_PAYMENT_DATE: "b956a778-1dd8-4b01-913e-ce75a52ecd04",
  PLANNED_PAYMENT_NOTE: "16e1b2b8-68c0-4699-b8cb-8a0fb4a180ed",
  STATUS: "9f40bdef-cee7-4842-bda0-bf4632c12180",
  TARGET_BUSINESS: "dce81bb2-0fa8-4b1a-9c57-9ecdfaf11ee3",
  TERMS: "21d39755-ba61-4861-b842-5d44e03b763f",
  TO: "d488b4aa-ed0e-4717-bbb3-3c634ae4546a",
  TOTAL_AMOUNT_DOLLARS: "b383588c-a877-41fa-9b1a-b826f2d26504",
  TOTAL_EX_TAX_AMOUNT_DOLLARS: "72d1cf3c-e14d-4758-b4f0-b7119f9c0c49",
  TOTAL_EX_TAX_AMOUNT_DOLLARS_EXPORTED: "fbe3b40f-a488-4112-91c1-8095f43ca041",
  TOTAL_TAX_AMOUNT_DOLLARS: "fbdc5d0f-3d23-4539-a0ef-1075833f557a",
  TOTAL_TAX_AMOUNT_DOLLARS_EXPORTED: "3de1ce50-4d1f-4d6e-9f03-33ee398d4c26",
  VENDOR_COMMISSION: "7a9ecda4-4110-426d-ae63-4a6873c5271d",
  VENDOR_NUMBER: "a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d",
  WARNINGS: "fcf46951-272e-464d-a87a-5547f76a6ec4",
  XERO_INVOICE_ID: "a6387c98-4404-4ec7-a0b6-41c7ba8399c4",
};

const BillingDocumentActionRenderer = (params: ICellRendererParams) => {
  const { data, api, node, context } = params;

  const { billingDocument, payments = [] } = data || {};

  const dispatch = useDispatch();

  const billingRun = useSelector(
    getBillingRunById(billingDocument?.billingRunId),
  );
  const isFinished = billingRun?.status === BillingRunStatus.FINISHED;

  const sundryTemplatesForBillingDocument = useSelector(
    getRelevantSundryTemplatesByBillingDocumentId(billingDocument?.id),
  );

  const sundryTemplateOptions = buildSundryTemplateOptions(
    // only build options for sundry templates that are marked for quick select
    sundryTemplatesForBillingDocument.filter(
      sundryTemplate => sundryTemplate.quickSelect,
    ),
  );

  const hasRetainProceedsPermission = useHasRetainedProceedsPermission();

  const hasManualChargePermission = useHasManualChargesPermission();

  const currentSpeciesId = useSelector(getCurrentSpeciesId);
  const labels = useSelector(getLabels);
  // If the user has these labels (by virtue of having ingested a rule book that uses them), they may be interested in using these controls.
  const RCTIRoundingAdjustmentLabel = Object.values(labels).find(
    label =>
      label.name === SystemLabelName.RCTI_ROUNDING_ADJUSTMENT &&
      label.species_id === currentSpeciesId,
  );
  const invoiceRoundingAdjustmentLabel = Object.values(labels).find(
    label =>
      label.name === SystemLabelName.INVOICE_ROUNDING_ADJUSTMENT &&
      label.species_id === currentSpeciesId,
  );

  const saleType = useSelector(getCurrentSale)?.sale_type;

  const isReferenceSale = saleType === SaleType.REFERENCE_SALE;

  const roundingManualAdjustmentIds = useSelector(
    getRoundingAdjustmentManualChargeIdsByBillingDocumentId(
      billingDocument?.id,
    ),
  );

  const paymentGroups = useMemo(() => groupPayments(payments), [payments]);

  if (!params.data || !billingDocument?.id) {
    return null;
  }

  const documentIsVoidedOrDeleted = InactiveIntegrationStatuses.includes(
    billingDocument.integrationStatus,
  );

  const allowActions = !billingDocument.isFetching;

  const hasChanges =
    // Check the has changes column is in the current table
    api.getColumnDef(BillingDocumentColId.HAS_CHANGES)
      ? // When the has changes column in is the table, check if there are no changes
        api.getCellValue({
          colKey: BillingDocumentColId.HAS_CHANGES,
          rowNode: node,
        }) !== "false-false"
      : // Otherwise don't allow the user to review the changes
        false;

  // When review is enabled, it should be the default action.
  // All other times - "View" should be the default action.
  const isReviewEnabled = !isReferenceSale && allowActions && hasChanges;

  const documentType = api.getCellValue({
    colKey: BillingDocumentColId.DOCUMENT_TYPE,
    rowNode: node,
  });

  function onClickEdit() {
    openModalLink(ModalTypes.EditBillingDocument, {
      id: billingDocument.id,
    });
  }

  function onClickReview() {
    openModalLink(ModalTypes.BillingDocumentReview, {
      id: billingDocument.id,
    });
  }

  function onClickRetainProceeds() {
    openModalLink(ModalTypes.RetainProceeds, {
      billingDocumentId: billingDocument.id,
    });
  }

  function onClickRegenerateDocument() {
    dispatch(
      BillingDocumentAction.regenerateDocument({
        id: billingDocument.id,
      }),
    );
  }

  const onClickApproveAndExport = () => {
    dispatch(
      BillingDocumentAction.acceptChangesAndExport({
        id: billingDocument.id,
      }),
    );
  };

  function onClickManageRoundingAdjustment() {
    // If we have an existing rounding adjustment, show it.
    const modalProps: {
      readOnlyFields: string[];
      gstMethod: string | undefined;
      fromBusinessId: string | undefined;
      toBusinessId: string | undefined;
      labels: string[] | undefined;
      id?: string;
    } = {
      readOnlyFields: ["fromBusinessId", "toBusinessId", "labels"],
      gstMethod: undefined,
      fromBusinessId: undefined,
      toBusinessId: undefined,
      labels: undefined,
    };

    if (!roundingManualAdjustmentIds) {
      // If there's none existing, go prefill some values.
      modalProps.gstMethod = GstMethod.GST_FIXED;

      if (billingDocument.type === BillingDocumentType.INVOICE) {
        // Create a manual adjustment with
        // - the INVOICE_ROUNDING_ADJUSTMENT label
        // - from the recipient of the funds (the agency)
        // - to the issuer of the funds (the buyer)
        modalProps.fromBusinessId = billingDocument.recipientBusinessId;
        modalProps.toBusinessId = billingDocument.issuerBusinessId;
        modalProps.labels = [invoiceRoundingAdjustmentLabel.id];
      } else if (billingDocument.type === BillingDocumentType.RCTI) {
        // Create a manual adjustment with
        // - the RCTI_ROUNDING_ADJUSTMENT label
        // - from the recipient of the funds (the vendor)
        // - to the issuer of the funds (the agency)
        modalProps.fromBusinessId = billingDocument.recipientBusinessId;
        modalProps.toBusinessId = billingDocument.issuerBusinessId;
        modalProps.labels = [RCTIRoundingAdjustmentLabel.id];
      }
    } else if (roundingManualAdjustmentIds.length >= 1) {
      // If we have an id - it's an edit rather than a create.
      modalProps.id = roundingManualAdjustmentIds[0];
      if (roundingManualAdjustmentIds.length > 1) {
        // If there's multiple entries here, they've likely been manually creating them - let the user know
        // things are a bit off.
        toast.error(
          "Multiple Rounding Adjustment Sundries exist for that document.  Please remove any extra, irrelevant entries from the Sundries tab.",
        );
      }
    }
    openModalLink(ModalTypes.ManualAdjustmentForm, modalProps);
  }

  function onClickAddSundryToBillingDocument(sundryTemplateId) {
    dispatch(
      openDispatchingModal(
        ModalTypes.ManualAdjustmentForm,
        null,
        {
          sundryTemplateId,
        },
        { billingDocumentId: billingDocument.id },
      ),
    );
  }

  const direction = getPaymentAdviceDirection(documentType);

  const emailBusinessId =
    documentType === BillingDocumentType.RCTI
      ? billingDocument.recipientBusinessId
      : billingDocument.issuerBusinessId;

  const paymentAdviceButtons = buildPaymentAdviceButtons({
    paymentGroups,
    direction,
    userRoleSlug: context.userRoleSlug,
    emailBusinessId,
    saleId: billingDocument.livestockSaleIds[0],
  });

  const canSeeSundryTemplateButtons =
    !isReferenceSale && hasManualChargePermission && sundryTemplateOptions;

  const sundryTemplateOptionsWithoutManual = sundryTemplateOptions.filter(
    to => to.label !== MANUAL_TEMPLATE_NAME,
  );

  const sundryTemplateButtons =
    (canSeeSundryTemplateButtons &&
      sundryTemplateOptionsWithoutManual.map(sundryTemplateOption => ({
        dataTour: `add${sundryTemplateOption.label}`,
        icon: faMoneyBills,
        onClick: () =>
          onClickAddSundryToBillingDocument(sundryTemplateOption.value),
        title: `Add ${sundryTemplateOption.label}`,
        tooltip: `Add a ${sundryTemplateOption.label} Sundry to this Billing Document`,
      }))) ||
    [];

  const buttons = [
    {
      dataTour: "viewBillingDocument",
      default: !isReviewEnabled,
      icon: faEye,
      isDisabled: false,
      onClick: undefined, // The button is a no-op but the event highlights the row
      title: "View",
      tooltip: "View the Billing Document",
    },
    !isReferenceSale && {
      dataTour: "editBillingDocument",
      default: false,
      icon: faPencil,
      isDisabled: false,
      onClick: onClickEdit,
      title: "Edit",
      tooltip: "Edit the Billing Document",
    },
    !isReferenceSale && {
      dataTour: "reviewBillingDocumentChanges",
      default: isReviewEnabled,
      icon: faExclamationCircle,
      isDisabled: !isReviewEnabled,
      onClick: onClickReview,
      title: "Review",
      tooltip: hasChanges
        ? "Review changes to the Billing Document"
        : "There are no changes to review",
    },
    !isReferenceSale &&
      documentType === BillingDocumentType.RCTI &&
      hasRetainProceedsPermission && {
        dataTour: "retainBillingDocumentProceeds",
        default: false,
        icon: faCommentsDollar,
        isDisabled: !allowActions,
        onClick: onClickRetainProceeds,
        title: "Retain Proceeds",
        tooltip: "Retain Proceeds for past due invoices",
      },

    !isReferenceSale &&
      hasManualChargePermission &&
      ((documentType === BillingDocumentType.RCTI &&
        RCTIRoundingAdjustmentLabel) ||
        (documentType === BillingDocumentType.INVOICE &&
          invoiceRoundingAdjustmentLabel)) && {
        dataTour: "manageRoundingAdjustment",
        default: false,
        icon: faUpDown,
        isDisabled: !allowActions,
        onClick: onClickManageRoundingAdjustment,
        title: "Rounding Adjustment",
        tooltip: "Manage a rounding adjustment for this document",
      },
    !isReferenceSale && {
      dataTour: "regenerateBillingDocument",
      default: false,
      icon: faRefresh,
      isDisabled: isFinished,
      onClick: onClickRegenerateDocument,
      title: "Regenerate Document",
      tooltip: "Regenerates the document without changing any data",
    },
    ...sundryTemplateButtons,
    !isReferenceSale &&
      hasManualChargePermission && {
        dataTour: "addSundryToBillingDocument",
        default: false,
        icon: faDollar,
        isDisabled: !allowActions,
        onClick: onClickAddSundryToBillingDocument,
        title: "Add Sundry",
        tooltip: "Add a Sundry to this Billing Document",
      },
    !isReferenceSale &&
      documentIsVoidedOrDeleted && {
        dataTour: "approveAndExport",
        default: false,
        icon: faPaperPlane,
        isDisabled: !allowActions,
        onClick: onClickApproveAndExport,
        title: "Approve and Export",
        tooltip: "Approve and Export to Xero",
      },
    ...paymentAdviceButtons,
  ].filter(Boolean);

  return <MultiButton buttons={buttons} ButtonComponent={SlimButton} />;
};

enum BillingDocumentActivityLabel {
  created = "Created",
  modified = "Modified",
  reversal = "Reversal",
  payment = "Payment",
  interest = "Interest Charged",
}

const openBillingDocumentActivityModal = (billingDocumentId: string) =>
  openModalLink(ModalTypes.BillingDocumentActivity, {
    billingDocumentId,
  });

const BillingDocumentActivityValueGetter = ({ data }: ICellRendererParams) => {
  if (!data) {
    return null;
  }
  const { billingDocument, payments } = data;

  if (!billingDocument) {
    return [null, null, null, null];
  }

  const { created, lastModified, hasBeenReversed, latestInterestChargeDate } =
    billingDocument;

  return [
    lastModified && lastModified > created
      ? BillingDocumentActivityLabel.modified
      : null,
    hasBeenReversed ? BillingDocumentActivityLabel.reversal : null,
    payments.length ? BillingDocumentActivityLabel.payment : null,
    latestInterestChargeDate ? BillingDocumentActivityLabel.interest : null,
  ];
};

interface ActivityCheck {
  shown: boolean;
  color: string;
  info: string;
}

interface ConditionalActivityTooltipProps {
  check: ActivityCheck;
  children: React.ReactNode;
}

const ConditionalActivityTooltip = ({
  check,
  children,
}: ConditionalActivityTooltipProps) => {
  if (check.shown) {
    return (
      <Tooltip
        title={check.info}
        iconPadding={space[0]}
        PopperProps={{ style: { marginTop: -18 } }}
      >
        {children}
      </Tooltip>
    );
  } else {
    return <div>{children}</div>;
  }
};

const BillingDocumentActivityRenderer = ({
  data,
  value,
}: ICellRendererParams) => {
  if (!data || !value) {
    return null;
  }

  const { billingDocument } = data;

  // If the value is a string, we are row grouping. This passes in a value like ",modified,reversal,,"
  // We need to split this string into and array to assign values and use the renderer
  const resolvedValue: (string | null)[] =
    typeof value === "string" ? value.split(",") : value;

  const [modified, reversal, payment, interest] = resolvedValue;

  const checks: ActivityCheck[] = [
    {
      // document exists so it has been created :)
      shown: true,
      color: colors.billingDocumentActivityCreated,
      info: BillingDocumentActivityLabel.created,
    },
    {
      shown: !!modified,
      color: colors.billingDocumentActivityModified,
      info: BillingDocumentActivityLabel.modified,
    },
    {
      shown: !!reversal,
      color: colors.billingDocumentActivityReversal,
      info: BillingDocumentActivityLabel.reversal,
    },
    {
      shown: !!payment,
      color: colors.billingDocumentActivityPayment,
      info: BillingDocumentActivityLabel.payment,
    },
    {
      shown: !!interest,
      color: colors.billingDocumentActivityInterest,
      info: BillingDocumentActivityLabel.interest,
    },
  ];

  return (
    <Row
      fullWidth
      className="justify-start cursor-pointer"
      onClick={() => openBillingDocumentActivityModal(billingDocument.id)}
    >
      {checks.map(check => (
        <React.Fragment key={check.info}>
          <ConditionalActivityTooltip check={check}>
            <FaIcon
              className={`m-2 text-12 ${check.shown ? "visible" : "invisible"}`}
              icon={faCircle}
              color={check.color}
            />
          </ConditionalActivityTooltip>
        </React.Fragment>
      ))}
    </Row>
  );
};

export const BillingDocumentColumnDef = {
  [BillingDocumentColId.DOCUMENT_NUMBER]: {
    colId: BillingDocumentColId.DOCUMENT_NUMBER,
    comparator: caseInsensitiveCompare,
    field: "billingDocument.number",
    headerName: "#",
    enableRowGroup: true,
    enablePivot: true,
    width: 50,
    filterParams: {
      keyCreator: ({ api, node, value }) => {
        return `${api.getCellValue({ colKey: BillingDocumentColId.DOCUMENT_TYPE, rowNode: node })}:${value} | ${api.getCellValue({ colKey: IssuerColId.ISSUER_NAME, rowNode: node })} -> ${api.getCellValue({ colKey: RecipientColId.RECIPIENT_NAME, rowNode: node })}`;
      },
      valueFormatter: params => params.value.name,
    },
  },
  [BillingDocumentColId.EMAIL_COUNT]: {
    colId: BillingDocumentColId.EMAIL_COUNT,
    field: "billingDocument.emailIds",
    headerName: "Sent Emails",
    valueGetter: getters.arrayLengthGetter,
    valueFormatter: emailCountRenderer,
    cellRenderer: "agGroupCellRenderer",
  },
  [BillingDocumentColId.LIVESTOCK_SALES]: {
    colId: BillingDocumentColId.LIVESTOCK_SALES,
    field: "billingDocument.livestockSaleIds",
    headerName: "Sale Id(s)",
    enableRowGroup: true,
  },

  [BillingDocumentColId.XERO_INVOICE_ID]: {
    colId: BillingDocumentColId.XERO_INVOICE_ID,
    field: "billingDocument.xeroInvoiceId",
    headerName: "Xero Invoice",
    enableRowGroup: true,
    // TODO: Deep linking to xero.
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      return data.billingDocument.xeroInvoiceId ? "Yes" : "";
    },
  },

  [BillingDocumentColId.DOCUMENT_TYPE]: {
    colId: BillingDocumentColId.DOCUMENT_TYPE,
    field: "billingDocument.type",
    headerName: "Type",
    cellRenderer: StatusRenderer,
    context: {
      statusRendererProps: {
        colorMap: BillingDocumentTypeColorMap,
      },
    } satisfies AggridColDefContext,
    width: 90,
  },

  [BillingDocumentColId.VENDOR_NUMBER]: {
    colId: BillingDocumentColId.VENDOR_NUMBER,
    field: "vendorNumber",
    headerName: "Vendor Number",
    enableRowGroup: true,
    comparator: caseInsensitiveCompare,
  },

  [BillingDocumentColId.VENDOR_COMMISSION]: {
    colId: BillingDocumentColId.VENDOR_COMMISSION,
    field: "effectiveVendorCommissions",
    headerName: "Vendor Commission",
    enableRowGroup: true,
    tooltipValueGetter:
      getters.effectiveVendorCommissionArrayTooltipValueGetter,
    valueGetter: getters.effectiveVendorCommissionArrayValueGetter,
  },

  [BillingDocumentColId.STATUS]: {
    colId: BillingDocumentColId.STATUS,
    field: "billingDocument.status",
    headerName: "Status",
    enableRowGroup: true,
    cellRenderer: StatusRenderer,
    context: {
      statusRendererProps: {
        colorMap: BillingDocumentStatusColorMap,
      },
    } satisfies AggridColDefContext,
  },

  [BillingDocumentColId.INTEGRATION_STATUS]: {
    headerName: "Integration Status",
    colId: BillingDocumentColId.INTEGRATION_STATUS,
    field: "billingDocument.integrationStatus",
    enableRowGroup: true,
    cellRenderer: StatusRenderer,
    context: {
      statusRendererProps: {
        colorMap: BillingDocumentIntegrationStatusColorMap,
      },
    } satisfies AggridColDefContext,
  },

  [BillingDocumentColId.INTEGRATION_AMOUNT_DIFFERENCE]: {
    headerName: "Integration Amount Difference ($)",
    colId: BillingDocumentColId.INTEGRATION_AMOUNT_DIFFERENCE,
    field: "billingDocument.id",
    headerTooltip:
      "The difference between the document amount in the integration compared to AgriNous." +
      " Should be 0 if the document is synchronised.",
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      const {
        integrationTotalTaxCents,
        integrationSubtotalCents,

        totalAmountDollars,
      } = data.billingDocument;

      if (
        integrationTotalTaxCents != null &&
        integrationSubtotalCents != null
      ) {
        const integrationTotalCents = Big(integrationSubtotalCents).plus(
          integrationTotalTaxCents,
        );
        const agrinousAmountCents = Big(totalAmountDollars).times(100);
        return centsDifferenceToDollars(
          integrationTotalCents,
          agrinousAmountCents,
        );
      }
      return null;
    },
    type: ColumnType.CURRENCY_FROM_CENTS,
  },

  [BillingDocumentColId.INTEGRATION_DUE_DIFFERENCE]: {
    headerName: "Integration Due Difference ($)",
    colId: BillingDocumentColId.INTEGRATION_DUE_DIFFERENCE,
    field: "billingDocument.id",
    headerTooltip:
      "The difference between the amount due in the integration compared to AgriNous." +
      " Should be 0 if the document is synchronised.",
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      const { payments, billingDocument } = data;
      const { integrationAmountDueCents, totalAmountDollars } = billingDocument;
      if (integrationAmountDueCents != null) {
        const totalAmountPaidCents = sumBy(payments, "totalAmountCents") || 0;

        const agrinousAmountDueCents = Big(totalAmountDollars)
          .times(100)
          .minus(totalAmountPaidCents);

        return centsDifferenceToDollars(
          Big(integrationAmountDueCents),
          agrinousAmountDueCents,
        );
      }
      return null;
    },
    type: ColumnType.CURRENCY_FROM_CENTS,
  },

  [BillingDocumentColId.WARNINGS]: {
    colId: BillingDocumentColId.WARNINGS,
    field: "billingDocument.warnings",
    headerName: "Warnings",
  },
  [BillingDocumentColId.DUE_DATE]: {
    colId: BillingDocumentColId.DUE_DATE,
    field: "billingDocument.dueDate",
    headerName: "Due Date",
    width: 90,
    type: ColumnType.DATE,
  },
  [BillingDocumentColId.HAS_CHANGES]: {
    colId: BillingDocumentColId.HAS_CHANGES,
    field: "billingDocument.hasFinancialChanges",
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      return [
        data.billingDocument.hasFinancialChanges,
        data.billingDocument.hasCosmeticChanges,
      ].join("-");
    },
    headerName: "Has Changes",
    enableRowGroup: true,
    cellRenderer: hasChangesRenderer,
  },
  [BillingDocumentColId.ACTIONS]: {
    colId: BillingDocumentColId.ACTIONS,
    field: "billingDocument.id",
    headerName: ACTION_COLUMN_NAME,
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      return [
        data.billingDocument.id,
        data.billingDocument.status,
        data.billingDocument.hasFinancialChanges,
        data.billingDocument.hasCosmeticChanges,
      ].join("-");
    },
    cellRenderer: BillingDocumentActionRenderer,
    pinned: "right",
    width: 150,
  },
  [BillingDocumentColId.OPEN_IN_NEW_WINDOW]: {
    colId: BillingDocumentColId.OPEN_IN_NEW_WINDOW,
    field: "billingDocument.id",
    headerName: "Document Number",
    enableRowGroup: true,
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      const { invoiceNumber } = data.billingDocument || {};
      return invoiceNumber;
    },
    cellRenderer: ({ data, context, value }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }

      const documentPreviewUrl = getBillingDocumentReportJobUrl(
        data.billingDocument.id,
        context.userRoleSlug,
        BillingDocumentReportJobStatus.COMMITTED,
      );

      return (
        <ActionButtonContainer>
          <ActionButton
            onClick={() => {
              window.open(documentPreviewUrl);
            }}
          >
            {value}
          </ActionButton>
        </ActionButtonContainer>
      );
    },
    comparator: caseInsensitiveCompare,
  },
  [BillingDocumentColId.TOTAL_AMOUNT_DOLLARS]: {
    colId: BillingDocumentColId.TOTAL_AMOUNT_DOLLARS,
    field: "billingDocument.totalAmountDollars",
    headerName: "Total ($)",
    type: ColumnType.CURRENCY,
    valueFormatter: formatters.dollarsCreditDebitFormatter,
  },
  [BillingDocumentColId.TOTAL_EX_TAX_AMOUNT_DOLLARS]: {
    colId: BillingDocumentColId.TOTAL_EX_TAX_AMOUNT_DOLLARS,
    field: "billingDocument.totalExTaxAmountDollars",
    headerName: "Amount ($)",
    type: ColumnType.CURRENCY,
    valueFormatter: formatters.dollarsCreditDebitFormatter,
  },
  [BillingDocumentColId.TOTAL_EX_TAX_AMOUNT_DOLLARS_EXPORTED]: {
    colId: BillingDocumentColId.TOTAL_EX_TAX_AMOUNT_DOLLARS_EXPORTED,
    field: "billingDocument.totalExTaxAmountDollarsExported",
    headerName: "Exported Amount ($)",
    type: ColumnType.CURRENCY,
    valueFormatter: formatters.dollarsCreditDebitFormatter,
  },
  [BillingDocumentColId.TOTAL_TAX_AMOUNT_DOLLARS]: {
    colId: BillingDocumentColId.TOTAL_TAX_AMOUNT_DOLLARS,
    field: "billingDocument.totalTaxAmountDollars",
    headerName: "Tax ($)",
    type: ColumnType.RIGHT_ALIGNED,
    valueFormatter: floatFormatter,
    context: {
      valueFormatterParams: {
        decimalPlaces: 2,
        formatToMinTwoDecimalPlaces: true,
      },
    } satisfies AggridColDefContext,
  },
  [BillingDocumentColId.TOTAL_TAX_AMOUNT_DOLLARS_EXPORTED]: {
    colId: BillingDocumentColId.TOTAL_TAX_AMOUNT_DOLLARS_EXPORTED,
    field: "billingDocument.totalTaxAmountDollarsExported",
    headerName: "Exported Tax ($)",
    type: ColumnType.RIGHT_ALIGNED,
    valueFormatter: floatFormatter,
    context: {
      valueFormatterParams: {
        decimalPlaces: 2,
        formatToMinTwoDecimalPlaces: true,
      },
    } satisfies AggridColDefContext,
  },
  [BillingDocumentColId.DOCUMENT_DOWNLOAD]: {
    colId: BillingDocumentColId.DOCUMENT_DOWNLOAD,
    field: "billingDocument.id",
    headerName: "Transaction document",
    valueGetter: () => "", // For the export to CSV
    cellRenderer: ({ data, context }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }

      const documentPreviewUrl = getBillingDocumentReportJobUrl(
        data.billingDocument.id,
        context.userRoleSlug,
        BillingDocumentReportJobStatus.COMMITTED,
      );
      return (
        <ActionButtonContainer>
          <ActionButton
            onClick={() => {
              window.open(documentPreviewUrl);
            }}
          >
            Download
          </ActionButton>
        </ActionButtonContainer>
      );
    },
  },
  [BillingDocumentColId.PAYMENT_REFS]: {
    headerName: "Payment Refs",
    colId: BillingDocumentColId.PAYMENT_REFS,
    field: "payments",
    filter: true,
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.payments?.length) return null;
      const seen = new Set();
      const labels = [];
      for (const payment of data.payments) {
        const key = payment.batchPaymentId || payment.id;
        if (!seen.has(key)) {
          seen.add(key);
          labels.push(getPaymentReferenceLabel(payment));
        }
      }
      return labels.join(", ");
    },
  },
  [BillingDocumentColId.PAID]: {
    headerName: "Paid ($)",
    colId: BillingDocumentColId.PAID,
    field: "payments",
    type: ColumnType.CURRENCY_FROM_CENTS,
    valueFormatter: formatters.dollarsCreditDebitFormatter,
    context: {
      valueFormatterParams: {
        decimalPlaces: 2,
      },
    } satisfies AggridColDefContext,
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data) {
        return null;
      }
      const { payments } = data;
      return Big(sumBy(payments, "totalAmountCents") || 0)
        .div(100)
        .toNumber();
    },
  },
  [BillingDocumentColId.ISSUED]: {
    headerName: "Exported",
    colId: BillingDocumentColId.ISSUED,
    field: "billingDocument.issuedDate",
    type: ColumnType.DATE,
  },
  [BillingDocumentColId.PAID_DATE]: {
    headerName: "Payment Date",
    colId: BillingDocumentColId.PAID_DATE,
    field: "payments",
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data) {
        return null;
      }
      const payments: { paymentDate: string }[] = get(data, "payments") || [];
      if (payments.length > 0) {
        return new Date(maxBy(payments, "paymentDate")?.paymentDate);
      }
      return null;
    },
    type: ColumnType.DATE,
  },
  [BillingDocumentColId.DAYS_LATE]: {
    headerName: "Days Late",
    colId: BillingDocumentColId.DAYS_LATE,
    valueGetter: ({ api, data, node }: ICellRendererParams) => {
      if (!data) {
        return null;
      }

      const { billingDocument } = data;
      if (!billingDocument) {
        return null;
      }

      // If the invoice was paid, how far before/after the due date was it?
      // NB - the paid date is the last date of payment - if this was PARTIAL payment,
      // the value may be somewhat misleading.

      const paidDate = api.getCellValue({
        colKey: BillingDocumentColId.PAID_DATE,
        rowNode: node,
      });

      if (
        paidDate &&
        billingDocument.dueDate &&
        billingDocument.integrationStatus ===
          BillingDocumentIntegrationStatus.PAID
      ) {
        return getDaysDifferent(
          dateTimeStringToDateTime(paidDate),
          dateTimeStringToDateTime(billingDocument.dueDate),
        );
      }
      // Otherwise, if we have a due date, but it's not paid, "today" is how overdue the invoice is
      if (billingDocument.dueDate) {
        return getDaysDifferent(
          new Date(),
          dateTimeStringToDateTime(billingDocument.dueDate),
        );
      }
      return null;
    },
    type: ColumnType.NUMERIC,
  },

  [BillingDocumentColId.DAYS_SINCE_PAYMENT]: {
    headerName: "Days Since Payment",
    colId: BillingDocumentColId.DAYS_SINCE_PAYMENT,
    valueGetter: ({ api, data, node }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      const { billingDocument } = data;
      // How many days since we paid?
      const paidDate = api.getCellValue({
        colKey: BillingDocumentColId.PAID_DATE,
        rowNode: node,
      });

      if (
        paidDate &&
        billingDocument.integrationStatus ===
          BillingDocumentIntegrationStatus.PAID
      ) {
        return getDaysDifferent(new Date(), dateTimeStringToDateTime(paidDate));
      }
      return null;
    },
    type: ColumnType.NUMERIC,
  },

  [BillingDocumentColId.DAYS_SINCE_DUE]: {
    headerName: "Days Since Due",
    colId: BillingDocumentColId.DAYS_SINCE_DUE,
    valueGetter: ({ data }: ICellRendererParams) => {
      if (!data?.billingDocument) {
        return null;
      }
      const { billingDocument } = data;
      // How many days since we this was due?
      if (billingDocument.dueDate) {
        return getDaysDifferent(
          new Date(),
          dateTimeStringToDateTime(billingDocument.dueDate),
        );
      }
      return null;
    },
    type: ColumnType.NUMERIC,
  },

  [BillingDocumentColId.TERMS]: {
    headerName: "Terms (Days)",
    colId: BillingDocumentColId.TERMS,
    headerTooltip:
      "Number of days between the sale date and the document due date.",
    valueGetter: ({ data }: ICellRendererParams) => {
      const dueDate = data?.billingDocument?.dueDate;
      // A document can span multiple livestock sales; using the first is a
      // simplification, same as the sibling DAYS_LATE/DAYS_SINCE_DUE columns
      // not accounting for multi-sale documents either.
      const saleDate = data?.livestockSales?.[0]?.date;
      if (!dueDate || !saleDate) {
        return null;
      }
      return getDaysDifferent(
        dateTimeStringToDateTime(dueDate),
        dateTimeStringToDateTime(saleDate),
      );
    },
    type: ColumnType.NUMERIC,
  },

  [BillingDocumentColId.CREATED]: {
    colId: BillingDocumentColId.CREATED,
    field: "billingDocument.created",
    headerName: "Created",
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },

  [BillingDocumentColId.LAST_MODIFIED]: {
    colId: BillingDocumentColId.LAST_MODIFIED,
    field: "billingDocument.lastModified",
    headerName: "Last Modified",
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  [BillingDocumentColId.LAST_PAID_INTEREST_DATE]: {
    headerName: "Last Paid Interest",
    colId: BillingDocumentColId.LAST_PAID_INTEREST_DATE,
    field: "billingDocument.latestInterestChargeDate",
    type: ColumnType.DATE,
  },
  [BillingDocumentColId.ACTIVITY]: {
    headerName: "Activity",
    colId: BillingDocumentColId.ACTIVITY,
    field: "billingDocument.activity",
    cellRenderer: BillingDocumentActivityRenderer,
    valueGetter: BillingDocumentActivityValueGetter,
    filter: "agSetColumnFilter",
    filterParams: {
      values: [
        BillingDocumentActivityLabel.modified,
        BillingDocumentActivityLabel.reversal,
        BillingDocumentActivityLabel.payment,
        BillingDocumentActivityLabel.interest,
      ],
    },
    enablePivot: true,
    enableRowGroup: true,
    width: 110,
  },
  // "billingReference" is labelled "External Ref" to match form labels across the UI
  [BillingDocumentColId.BILLING_REFERENCE]: {
    headerName: "External Ref",
    colId: BillingDocumentColId.BILLING_REFERENCE,
    field: "billingDocument.billingReference",
    type: ColumnType.STRING,
  },
  [BillingDocumentColId.PLANNED_PAYMENT_DATE]: {
    colId: BillingDocumentColId.PLANNED_PAYMENT_DATE,
    field: "billingDocument.plannedPaymentDate",
    headerName: "Planned Payment Date",
    width: 90,
    type: ColumnType.DATE,
  },
  [BillingDocumentColId.PLANNED_PAYMENT_NOTE]: {
    headerName: "Planned Payment Note",
    colId: BillingDocumentColId.PLANNED_PAYMENT_NOTE,
    field: "billingDocument.plannedPaymentNote",
    type: ColumnType.STRING,
  },
  [BillingDocumentColId.BILLING_ROLE]: {
    colId: BillingDocumentColId.BILLING_ROLE,
    enableRowGroup: true,
    field: "billingDocument.billingRole",
    headerName: "Billing Role",
    comparator: (a: string | null, b: string | null) => {
      const order = (value: string | null) => {
        if (value === BillingRole.VENDOR) return 0;
        if (value === BillingRole.BUYER) return 1;
        if (value?.startsWith(BillingRole.OTHER_CREDITOR)) return 2;
        if (value?.startsWith(BillingRole.OTHER_DEBTOR)) return 3;
        return 4;
      };
      const diff = order(a) - order(b);
      if (diff !== 0) return diff;
      return (a ?? "").localeCompare(b ?? "");
    },
  },
  [BillingDocumentColId.CALCULATED_BILLING_REFERENCE]: {
    colId: BillingDocumentColId.CALCULATED_BILLING_REFERENCE,
    field: "billingDocument.calculatedBillingReference",
    headerName: "Billing Ref",
    type: ColumnType.STRING,
  },
};

export const FixWarningsActionColumn = {
  colId: BillingDocumentColId.ACTION_FIX,
  field: "business.id",
  headerName: ACTION_COLUMN_NAME,
  cellRenderer: function Component({ colDef, data }: ICellRendererParams) {
    const dispatch = useDispatch();
    if (!data) {
      return null;
    }

    const businessId = get(data, colDef.field);

    const { billingDocument, warnings } = data;

    const hasWarnings = Boolean(warnings.length);

    if (!hasWarnings) {
      return null;
    }

    const onClickGoToExport = () => {
      goToBillingStep(BillingScreens.EXPORT);
    };

    const onClickEditBusiness = () => {
      openEditBusinessModal(
        businessId,
        null,
        BusinessModalSection.FINANCE_AND_ACCOUNTING,
      );
    };

    const onClickApproveAndExport = () => {
      dispatch(
        BillingDocumentAction.acceptChangesAndExport({
          id: billingDocument.id,
        }),
      );
    };

    const BillingDocumentHasExportErrors =
      billingDocument.exportErrors || billingDocument.exportWarnings;

    const billingDocumentIsVoidedOrDeletedInIntegration =
      InactiveIntegrationStatuses.includes(billingDocument.integrationStatus);

    return (
      <ActionButtonContainer>
        {!billingDocumentIsVoidedOrDeletedInIntegration && (
          <ActionButton
            onClick={
              BillingDocumentHasExportErrors
                ? onClickGoToExport
                : onClickEditBusiness
            }
          >
            <FontAwesomeIcon icon={faWrench} />
            &nbsp;Review
          </ActionButton>
        )}

        {billingDocumentIsVoidedOrDeletedInIntegration && (
          <ActionButton onClick={onClickApproveAndExport}>
            <FontAwesomeIcon icon={lightFaPaperPlane} />
            &nbsp;Approve and Export
          </ActionButton>
        )}
      </ActionButtonContainer>
    );
  },
};
